import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import type { PodcastTaxonomyProgramRow } from "../../services/api";
import TopicTreemap from "./TopicTreemap";
import {
  buildTopicHierarchyFromAggregate,
  programTopicTotal,
  type TopicMetric,
} from "./taxonomyHierarchy";

const BASE_TREEMAP_HEIGHT = 220;
const MIN_TREEMAP_HEIGHT = 120;

function formatViews(n: number): string {
  return n.toLocaleString("en-US");
}

function formatMetricValue(value: number, metric: TopicMetric): string {
  if (metric === "weightedMentions") {
    return `${formatViews(Math.round(value))} view-weighted`;
  }
  return `${value} mentions`;
}

type ProgramTreemapGridProps = {
  programs: PodcastTaxonomyProgramRow[];
  colorBySlug: Map<string, string>;
  metric: TopicMetric;
  legendTopics: Array<{ slug: string; display: string }>;
  onExpandProgram: (programId: string | null) => void;
};

const ProgramTreemapGrid: React.FC<ProgramTreemapGridProps> = ({
  programs,
  colorBySlug,
  metric,
  legendTopics,
  onExpandProgram,
}) => {
  const programsWithTaxonomy = useMemo(
    () => programs.filter((program) => program.metrics.episodesWithTaxonomy > 0),
    [programs],
  );

  const maxProgramTotal = useMemo(() => {
    if (programsWithTaxonomy.length === 0) return 1;
    return Math.max(
      1,
      ...programsWithTaxonomy.map((program) => programTopicTotal(program, metric)),
    );
  }, [programsWithTaxonomy, metric]);

  const legendItems = useMemo(
    () =>
      legendTopics
        .filter((topic) => colorBySlug.has(topic.slug))
        .slice(0, 12)
        .map((topic) => ({
          ...topic,
          color: colorBySlug.get(topic.slug) ?? "#8884d8",
        })),
    [legendTopics, colorBySlug],
  );

  if (programsWithTaxonomy.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No programs with taxonomy in this period.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      {legendItems.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={0.75} alignItems="center">
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
            Topics:
          </Typography>
          {legendItems.map((item) => (
            <Chip
              key={item.slug}
              size="small"
              label={item.display}
              sx={{
                bgcolor: item.color,
                color: "#fff",
                fontWeight: 500,
                "& .MuiChip-label": { px: 1 },
              }}
            />
          ))}
          {legendTopics.length > legendItems.length && (
            <Chip size="small" variant="outlined" label="Others" />
          )}
        </Stack>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            lg: "repeat(3, 1fr)",
          },
          gap: 2,
        }}
      >
        {programsWithTaxonomy.map((program) => {
          const total = programTopicTotal(program, metric);
          const scale = total / maxProgramTotal;
          const treemapHeight = Math.max(
            MIN_TREEMAP_HEIGHT,
            Math.round(BASE_TREEMAP_HEIGHT * scale),
          );
          const hierarchy = buildTopicHierarchyFromAggregate(program.topics, metric);

          return (
            <Card key={program.programId ?? "unassigned"} variant="outlined">
              <CardActionArea onClick={() => onExpandProgram(program.programId)}>
                <CardContent sx={{ pb: 1.5 }}>
                  <Stack spacing={1}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {program.programName}
                      </Typography>
                      <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`${program.metrics.episodesWithTaxonomy} ep w/ taxonomy`}
                        />
                        <Chip
                          size="small"
                          variant="outlined"
                          label={formatMetricValue(total, metric)}
                        />
                      </Stack>
                    </Box>
                    {hierarchy.length > 0 ? (
                      <TopicTreemap
                        hierarchy={hierarchy}
                        drillDown={false}
                        compact
                        height={treemapHeight}
                        colorBySlug={colorBySlug}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        No topics assigned
                      </Typography>
                    )}
                    <Typography variant="caption" color="primary">
                      Click to expand
                    </Typography>
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>
    </Stack>
  );
};

export default ProgramTreemapGrid;
